import { useNavigate, useSearch } from "@tanstack/react-router"
import type { ExpandedState, OnChangeFn, Row, RowSelectionState, SortingState } from "@tanstack/react-table"
import type { FC } from "react"
import { useCallback, useEffect, useMemo, useRef } from "react"
import { useShallow } from "zustand/react/shallow"

import { usePurchaseOrderTable } from "../hooks/usePurchaseOrderTable"

import { PurchaseOrdersExpandedRowDetails } from "./PurchaseOrdersExpandedRowDetails"

import { ErrorMessage } from "@/components/ErrorMessage/ErrorMessage"
import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import { DEBOUNCE_DELAY_MS } from "@/constants/common"
import type { OrderLine } from "@/graphql/codegen/graphql"
import { SortingOrder } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import { useProductPurchaseOrdersSearchStore } from "@/screens/Procurement/PurchaseOrders/stores/useProductPurchaseOrdersSearchStore"
import type { SortablePurchaseOrderAccessorKey } from "@/screens/Procurement/PurchaseOrders/types"
import { accessoryKeyToOrderSortField } from "@/screens/Procurement/PurchaseOrders/types"
import { getProductOrderColumns } from "@/screens/Procurement/PurchaseOrders/utils/productOrderColumns"
import useDebounce from "@/utils/useDebounce"

interface PurchaseProductOrdersTableProps {
  onUploadClick: (orderId: string) => void
  canUpload: boolean
}

export const PurchaseProductOrdersTable: FC<PurchaseProductOrdersTableProps> = ({ onUploadClick, canUpload }) => {
  const navigate = useNavigate()
  const currentSearch = useSearch({
    from: "/app/procurement/_layout/purchase-orders",
  })
  const { category, query, field, order } = currentSearch
  const data = useProductPurchaseOrdersSearchStore(({ data }) => data)
  const fetching = useProductPurchaseOrdersSearchStore(({ fetching }) => fetching)
  const error = useProductPurchaseOrdersSearchStore(({ error }) => error)
  const executeSearch = useProductPurchaseOrdersSearchStore(useShallow((state) => state.executeSearch))
  const loadMore = useProductPurchaseOrdersSearchStore(useShallow((state) => state.loadMore))
  const clearStore = useProductPurchaseOrdersSearchStore(({ clearStore }) => clearStore)

  const columns = useMemo(() => getProductOrderColumns({ onUploadClick, canUpload }), [onUploadClick, canUpload])

  const { expanded, selected, sorting, setExpanded, setSelected } = usePurchaseOrderTable({
    sortBy: field,
    sortOrder: order,
    category,
  })

  const tableContainerRef = useRef<HTMLDivElement>(null)

  const debouncedExecuteSearch = useDebounce((filters: any) => {
    executeSearch(queryClient, filters)
  }, DEBOUNCE_DELAY_MS)

  const handleLoadMore = useCallback(() => {
    if (currentSearch && !fetching) {
      loadMore(queryClient, currentSearch)
    }
  }, [currentSearch, fetching, loadMore])

  const handleSortingChange = useCallback(
    (updaterFn: (state: SortingState) => SortingState) => {
      const newSorting = updaterFn(sorting)
      if (newSorting.length > 0) {
        const sort = newSorting[0]
        const key = sort.id as SortablePurchaseOrderAccessorKey

        const field = accessoryKeyToOrderSortField.get(key)
        void navigate({
          search: (prev) => ({
            ...prev,
            query,
            category,
            field,
            order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc,
          }),
          to: "/app/procurement/purchase-orders",
          replace: true,
        })
      }
    },
    [category, navigate, query, sorting]
  )

  const onScroll = useScrollLoadMore({
    fetching,
    loadMore: handleLoadMore,
    scrollContainerRef: tableContainerRef,
  })

  // Execute search when filters change
  useEffect(() => {
    debouncedExecuteSearch(currentSearch)
  }, [debouncedExecuteSearch, currentSearch])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [clearStore])

  if (error) {
    return <ErrorMessage error={error} />
  }

  return (
    <AerosBaseTable<OrderLine>
      error={error}
      features={{
        table: {
          stickyHeader: true,
        },
        tanstackOptions: {
          columns,
          data,
          state: {
            expanded,
            rowSelection: selected,
            sorting,
            isLoading: fetching,
          },
          enableRowSelection: true,
          enableMultiRowSelection: true,
          enableExpanding: true,
          getRowCanExpand: (row: Row<OrderLine>) => Boolean(row.original.orderLineId),
          onRowSelectionChange: setSelected as OnChangeFn<RowSelectionState>,
          onExpandedChange: setExpanded as OnChangeFn<ExpandedState>,
          getRowId: (row) => row.orderLineId,
          renderExpandedContent: PurchaseOrdersExpandedRowDetails,
          onSortingChange: handleSortingChange as OnChangeFn<SortingState>,
          enableSortingRemoval: false,
          manualSorting: true,
          sortDescFirst: false,
        },
      }}
      slotProps={{
        bodyCellExpanded: { className: "bg-primary-100" },
        container: {
          ref: tableContainerRef,
          onScroll,
          sx: { height: "inherit", position: "relative" },
        },
      }}
      noDataMessage='No products found in purchase orders'
    />
  )
}
